// Hook PATCH per-field pour le DAL mobile.
// Chaque champ appelle `patch(path, value)` au onBlur (input/textarea) ou onChange
// (select/checkbox/toggle). La requête est tirée immédiatement, AbortController
// annule les requêtes pending si on patch deux fois le même path en succession
// rapide (rare). Voir backend dal_patch.py pour la whitelist server-side.

import { useCallback, useRef, useState } from "react";
import { validatePatchPath, getDalAuthToken } from "../components/dal/dalState";
import { enteteTenant } from "../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

export default function usePatchDAL({ dossierId, tenantId, zoneIds }) {
  const [status, setStatus] = useState("idle");          // idle | saving | saved | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const inflightRef = useRef({});                         // { [path]: AbortController }

  const patch = useCallback(async (path, value) => {
    if (!dossierId) return false;
    if (!validatePatchPath(path, zoneIds)) {
      setStatus("error");
      setErrorMsg(`Path invalide : ${path}`);
      return false;
    }

    // Annule un PATCH précédent sur le MÊME path (l'utilisateur a re-tapé entre temps)
    const previous = inflightRef.current[path];
    if (previous) previous.abort();
    const ac = new AbortController();
    inflightRef.current[path] = ac;

    setStatus("saving");
    setErrorMsg(null);
    try {
      const token = getDalAuthToken();
      const r = await fetch(`${API}/api/dal/save-mobile/${dossierId}`, {
        method: "PATCH",
        signal: ac.signal,
        headers: {
          "Content-Type": "application/json",
          ...enteteTenant(tenantId),
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ path, value }),
      });
      if (r.ok) {
        setStatus("saved");
        setLastSavedAt(new Date());
        delete inflightRef.current[path];
        return true;
      }
      let detail = `${r.status}`;
      try { const j = await r.json(); detail = j.detail || j.error || detail; } catch {}
      setStatus("error");
      setErrorMsg(detail);
      return false;
    } catch (err) {
      if (err.name === "AbortError") return false;
      setStatus("error");
      setErrorMsg(err.message || "réseau");
      return false;
    }
  }, [dossierId, tenantId, zoneIds]);

  return { patch, status, lastSavedAt, errorMsg };
}
